import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { AppShell } from "@/components/AppShell";
import { brandAssets } from "@/constants/brand";
import { createRoom, getRooms, joinRoom } from "@/api";
import { useAuth } from "@/context/AuthContext";

export default function DashboardPage() {
  const { user } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [status, setStatus] = useState("");
  const [roomForm, setRoomForm] = useState({ name: "", description: "" });
  const [joinCode, setJoinCode] = useState("");

  const loadRooms = async () => {
    try {
      const { data } = await getRooms();
      setRooms(data);
    } catch (error) {
      setStatus("Unable to reach the war rooms right now.");
    }
  };

  useEffect(() => {
    loadRooms();
  }, []);

  const handleCreateRoom = async (event) => {
    event.preventDefault();
    setStatus("");
    try {
      await createRoom(roomForm);
      setRoomForm({ name: "", description: "" });
      await loadRooms();
    } catch (error) {
      setStatus("The sanctum rejected this room.");
    }
  };

  const handleJoinRoom = async (event) => {
    event.preventDefault();
    setStatus("");
    if (!joinCode) {
      setStatus("Provide a room code to join.");
      return;
    }
    try {
      await joinRoom(joinCode);
      setJoinCode("");
      await loadRooms();
    } catch (error) {
      setStatus("No room with that sigil exists.");
    }
  };

  return (
    <AppShell>
      <section className="space-y-10" data-testid="dashboard-page">
        <div className="flex flex-col gap-3" data-testid="dashboard-header">
          <p className="text-xs uppercase tracking-[0.4em] text-stone-500" data-testid="dashboard-eyebrow">
            Welcome back
          </p>
          <h1 className="font-cinzel text-3xl" data-testid="dashboard-title">
            {user?.username}'s War Rooms
          </h1>
          <p className="text-stone-400" data-testid="dashboard-description">
            Forge new campaigns or step back into the dark realms already bound to you.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-testid="dashboard-forms">
          <form className="panel-iron rounded-xl p-8 space-y-4" onSubmit={handleCreateRoom} data-testid="create-room-form">
            <h2 className="font-cinzel text-xl" data-testid="create-room-title">
              Forge a new room
            </h2>
            <Input
              value={roomForm.name}
              onChange={(event) => setRoomForm((prev) => ({ ...prev, name: event.target.value }))}
              placeholder="Room name"
              className="bg-stone-950 border-stone-800 text-stone-200"
              data-testid="create-room-name-input"
              required
            />
            <Textarea
              value={roomForm.description}
              onChange={(event) => setRoomForm((prev) => ({ ...prev, description: event.target.value }))}
              placeholder="Campaign intent"
              className="bg-stone-950 border-stone-800 text-stone-200"
              data-testid="create-room-description-input"
            />
            <Button
              type="submit"
              className="bg-red-900 hover:bg-red-800 text-stone-100 font-cinzel uppercase tracking-[0.25em]"
              data-testid="create-room-submit-button"
            >
              Raise the Gate
            </Button>
          </form>

          <form className="panel-iron rounded-xl p-8 space-y-4" onSubmit={handleJoinRoom} data-testid="join-room-form">
            <h2 className="font-cinzel text-xl" data-testid="join-room-title">
              Enter via sigil
            </h2>
            <Input
              value={joinCode}
              onChange={(event) => setJoinCode(event.target.value)}
              placeholder="Paste room ID"
              className="bg-stone-950 border-stone-800 text-stone-200"
              data-testid="join-room-code-input"
            />
            <Button
              type="submit"
              variant="secondary"
              className="border border-stone-700 text-stone-200 hover:text-stone-100"
              data-testid="join-room-submit-button"
            >
              Join the War Room
            </Button>
          </form>
        </div>

        {status && (
          <div className="bg-red-900/30 border border-red-900/50 text-red-200 px-4 py-3 rounded-lg" data-testid="dashboard-status">
            {status}
          </div>
        )}

        <div className="space-y-6" data-testid="rooms-list">
          <div className="flex items-center justify-between" data-testid="rooms-header">
            <h2 className="font-cinzel text-2xl" data-testid="rooms-title">
              Active Campaigns
            </h2>
            <p className="text-stone-500" data-testid="rooms-count">
              {rooms.length} bound halls
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="rooms-grid">
            {rooms.map((room) => (
              <div key={room.id} className="panel-iron rounded-2xl overflow-hidden" data-testid={`room-card-${room.id}`}>
                <div className="h-40 w-full overflow-hidden" data-testid={`room-card-image-${room.id}`}>
                  <img
                    src={brandAssets.mapPlaceholder}
                    alt="Room map"
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-stone-500" data-testid={`room-card-label-${room.id}`}>
                      War Room
                    </p>
                    <h3 className="font-cinzel text-xl" data-testid={`room-card-title-${room.id}`}>
                      {room.name}
                    </h3>
                    <p className="text-stone-400 text-sm mt-2" data-testid={`room-card-description-${room.id}`}>
                      {room.description || "A realm without recorded lore."}
                    </p>
                  </div>
                  <p className="text-xs uppercase tracking-[0.3em] text-stone-500" data-testid={`room-card-members-${room.id}`}>
                    Members: {room.member_ids?.length || 1}
                  </p>
                  <Link to={`/rooms/${room.id}`} data-testid={`room-card-link-${room.id}`}>
                    <Button
                      className="w-full bg-red-900 hover:bg-red-800 text-stone-100 font-cinzel uppercase tracking-[0.25em]"
                      data-testid={`room-card-button-${room.id}`}
                    >
                      Enter Room
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </AppShell>
  );
}
